import BaseStore from '../../components/base/base-store';
import _ from 'lodash';
import IntlMessageFormat from 'intl-messageformat';
import { defaultLocale } from './default-locale';
import { APP_PUBLIC_PLAYER_HOST_ID, APP_PUBLIC_PLAYER_SETTINGS_HOST } from './settings-constants';
import {
    IEnvironmentSettingsState,
    IPublicPlayerOptions,
    ISettingsStoreState
} from './settings-types';

interface IStringHierarchy {
    [key: string]: string | IStringHierarchy;
}

function reGroupValuesList(list: { [key: string]: string }): IStringHierarchy {
    const res = {} as IStringHierarchy;
    _.forOwn(list, (value: string, key: string) => {
        const path = key.split('.');
        let head = res;
        _.each(path, (part, index) => {
            if (typeof head === 'string') {
                console.warn('INCORRECT MESSAGE GROUP:' + key, head);
                return;
            }
            head[part] = head[part] || {};
            if (index === path.length - 1) {
                head[part] = value;
            }
            head = head[part] as IStringHierarchy;
        });
    });
    return res;
}

const displayedWarnings = {};

function defaultsWarn<T>(settingName: string, settingValue: T): void {
    if (!displayedWarnings[settingName]) {
        displayedWarnings[settingName] = true;
        console.debug('Using default value for ' + settingName, settingValue);
    }
}

class SettingsStore extends BaseStore<ISettingsStoreState> {
    public dispatcherEvents: string = 'setSettingsStore';

    public initialState: ISettingsStoreState = {
        sources: {
            system: {},
            environment: {
                locale: 'ru',
                instanceId: 0,
                userProfile: {
                    roles: [],
                    permissions: {}
                },
                archiveDumpQuality: [],
                localVideoQualityPresets: []
            },
            user: {
                locale: 'ru'
            },
            i18n: {},
            paths: {}
        },
        publicPlayerHost: '',
        loaded: false
    };

    private _ready: (() => void)[] = [];

    constructor() {
        super();
        this.initialize('SettingsStore');
    }

    public getSystemSetting<T>(settingName: string): T;
    /**
     * @deprecated do not use default value
     */
    // tslint:disable-next-line:unified-signatures (autogenerated)
    public getSystemSetting<T>(settingName: string, defaults: T): T;
    public getSystemSetting<T>(settingName: string, defaults?: T): T {
        let settingValue: T;
        try {
            settingValue = this.getState().sources.system[settingName] as T;
        } catch (error) {
            settingValue = defaults;
            defaultsWarn(settingName, settingValue);
        }
        if (typeof settingValue === 'undefined') {
            settingValue = defaults;
            defaultsWarn(settingName, settingValue);
        }
        return settingValue;
    }

    public getJsonSetting<T>(settingName: string): T;
    /**
     * @deprecated do not use default value
     */
    // tslint:disable-next-line:unified-signatures (autogenerated)
    public getJsonSetting<T>(settingName: string, defaults: T): T;
    public getJsonSetting<T>(settingName: string, defaults?: T): T {
        let settingValue: T;
        try {
            const sourceValue = this.getState().sources.system[settingName] as T;
            settingValue =
                typeof sourceValue === 'string' ? (JSON.parse(sourceValue) as T) : sourceValue;
        } catch (error) {
            settingValue = defaults;
            defaultsWarn(settingName, settingValue);
        }
        return settingValue;
    }

    public getLocale(): string {
        try {
            return this.getImmediateState().sources.environment.locale || defaultLocale;
        } catch (error) {
            return defaultLocale;
        }
    }

    public getIntlData(): {
        locales: string[];
        messages: IStringHierarchy;
    } {
        return {
            locales: [this.getLocale()],
            messages: reGroupValuesList(this.getState().sources.i18n || {})
        };
    }

    public getI18nMessages(): { [messageId: string]: string } {
        return this.getImmediateState().sources.i18n;
    }

    /**
     * bound to object for direct use
     */
    public getIntlMessage = (messageId: string): string => {
        return (this.getState().sources.i18n || {})[messageId] || '';
    };

    public getEnvironment(): IEnvironmentSettingsState {
        return this.getState().sources.environment || ({} as IEnvironmentSettingsState);
    }

    public getPublicPlayerHost(): string {
        if (typeof APP_PUBLIC_PLAYER_HOST_b192a8f0 !== 'undefined') {
            return APP_PUBLIC_PLAYER_HOST_b192a8f0;
        }
        return (
            this.getState().publicPlayerHost || (window[APP_PUBLIC_PLAYER_HOST_ID] as string) || ''
        );
    }

    public getPublicPlayerSettingsHost(): string {
        if (typeof APP_PUBLIC_PLAYER_SETTINGS_HOST_b192a8f0 !== 'undefined') {
            return APP_PUBLIC_PLAYER_SETTINGS_HOST_b192a8f0;
        }
        return (window[APP_PUBLIC_PLAYER_SETTINGS_HOST] as string) || '';
    }

    private playerOptions(): {
        [playerId: string]: IPublicPlayerOptions;
    } {
        return (
            this.getImmediateState().publicPlayerOptionsById ||
            ({} as {
                [playerId: string]: IPublicPlayerOptions;
            })
        );
    }

    public getPublicPlayerOptions(playerId: string): IPublicPlayerOptions {
        return this.playerOptions()[playerId];
    }

    public getPublicPlayerAuthToken(): string {
        return this.getState().publicPlayerAuthToken;
    }

    public async waitForAuth(): Promise<void> {
        const state = this.getState();
        if (state.publicPlayerAuthReady === false) {
            return new Promise<void>((resolve, reject) => {
                this._ready.push(resolve);
            });
        }
        return Promise.resolve();
    }

    public setPublicPlayerAuthToken(publicPlayerAuthToken: string): void {
        this.updateState({ publicPlayerAuthToken });
    }

    public setPublicPlayerOptions(
        publicPlayerId: string,
        publicPlayerOptions: IPublicPlayerOptions
    ): void {
        const tokens = this.playerOptions();
        this.updateState({
            publicPlayerOptionsById: { ...tokens, [publicPlayerId]: publicPlayerOptions }
        });
    }

    public removePublicPlayerOptions(publicPlayerId: string): void {
        const { [publicPlayerId]: _unused, ...tokens } = this.playerOptions();
        this.updateState({
            publicPlayerOptionsById: { ...tokens }
        });
    }

    public setPublicPlayerAuthReady(publicPlayerAuthReady: boolean): void {
        if (publicPlayerAuthReady === true) {
            this._ready.forEach(r => r());
        }
        this.updateState({ publicPlayerAuthReady });
    }

    public formatMessage = (message: string, values: { [valueKey: string]: string }) => {
        const res = new IntlMessageFormat(message, this.getLocale());
        return res.format(values);
    };

    public updateState(state: Partial<ISettingsStoreState>): void {
        this.innerUpdateState(state);
    }
}

const store = new SettingsStore();

export function getIntlMessage(messageId: string): string {
    if (!messageId) {
        console.error('Undefined message id!');
    }
    return store.getIntlMessage(messageId);
}

// tslint:disable-next-line:no-default-export (autogenerated)
export default store;
